/**
 * DualClinicianEmergingNotes
 *
 * Shared read-only Emerging Formulation / Confidential Notes UI for the
 * Diagnostic Decision Review and Final Formulation pages.
 *
 * Why two modes?
 * - SLaM / 3 Borough: the SSI clinician and the Lead each leave their own
 *   emerging notes. Showing them side by side lets the Lead (or MDT) compare
 *   and, on Final Formulation, copy from either into the editable box.
 * - TOC / single-clinician: one full-width panel from the assessing clinician
 *   (legacy CI section 12 / SSI DF paths via useEmergingFormulations fallback).
 *
 * Visual language matches the amber "Not included in final report" panels used
 * on SSI Review / CI Review. A grey variant is available for TOC Final
 * Formulation, which historically used a neutral read-only card.
 */

import React from 'react';
import { formatDateTime, isValidTimestamp } from '../../../../utils/timestampUtils';
import { ReadOnlyAutoResizeTextarea } from '../../../../components/AutoResizeTextarea';

/**
 * One read-only notes card (formulation or confidential).
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.authorName] - Display name shown as "From …"
 * @param {string} [props.roleLabel] - e.g. "SSI clinician" / "Lead clinician"
 * @param {string} props.text
 * @param {*} [props.lastUpdated] - Firestore Timestamp or Date-like value
 * @param {string} props.emptyMessage
 * @param {string} props.ariaLabel
 * @param {'amber'|'gray'} [props.variant='amber']
 * @param {boolean} [props.showNonReportFooter=true]
 */
export function ReadOnlyNotesCard({
  title,
  authorName = null,
  roleLabel = null,
  text = '',
  lastUpdated = null,
  emptyMessage,
  ariaLabel,
  variant = 'amber',
  showNonReportFooter = true,
}) {
  const isAmber = variant === 'amber';
  const shellClass = isAmber
    ? 'bg-amber-50 border-amber-200'
    : 'bg-gray-50 border-gray-200';
  const textareaClass = isAmber
    ? 'bg-white/70 border-amber-200 opacity-90'
    : 'bg-gray-100 border-gray-200 opacity-75';
  const authorClass = isAmber ? 'text-amber-800/80' : 'text-gray-500';
  const footerClass = isAmber
    ? 'text-amber-900/80'
    : 'text-gray-500';

  return (
    <div
      className={`${shellClass} rounded-2xl border p-6 flex flex-col h-full text-left`}
    >
      {/* Title + author stay on one row. "Last updated" always sits on the next
          line so a longer attribution (or its absence) never shoves the
          timestamp into a different place on neighbouring cards. Colour matches
          the "Not included in final report" footer so it stays legible on amber. */}
      <div className="mb-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-lg font-semibold">{title}</h3>
          {(authorName || roleLabel) && (
            <span className={`text-xs ${authorClass}`}>
              From {authorName || roleLabel}
              {authorName && roleLabel ? ` (${roleLabel})` : ''}
            </span>
          )}
        </div>
        {isValidTimestamp(lastUpdated) && (
          <p className={`text-xs italic ${footerClass} mt-1 mb-0`}>
            Last updated {formatDateTime(lastUpdated)}
          </p>
        )}
      </div>

      {text ? (
        <ReadOnlyAutoResizeTextarea
          value={text}
          className={`w-full p-4 border rounded-lg text-base text-gray-700 resize-none cursor-not-allowed text-left ${textareaClass}`}
          ariaLabel={ariaLabel}
        />
      ) : (
        <p className="text-sm text-gray-400 italic">{emptyMessage}</p>
      )}

      {showNonReportFooter && (
        <p className={`text-xs italic ${footerClass} text-right mt-2 mb-0`}>
          Not included in final report
        </p>
      )}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.showDual - When true, render SSI | Lead columns
 * @param {Object|null} props.ssiClinician - Normalised emerging data (or null)
 * @param {Object|null} props.leadClinician - Normalised emerging data (or null)
 * @param {string} props.ssiDisplayName
 * @param {string} props.leadDisplayName
 * @param {*} [props.ssiLastUpdated]
 * @param {*} [props.leadLastUpdated]
 * @param {boolean} [props.showConfidential=true] - Hide on Final Formulation
 * @param {string} [props.singleFormulation=''] - TOC / single-clinician text
 * @param {string} [props.singleConfidential='']
 * @param {*} [props.singleLastUpdated]
 * @param {string|null} [props.singleAuthorName]
 * @param {'amber'|'gray'} [props.singleVariant='amber']
 * @param {boolean} [props.alwaysShowConfidential=false] - SLaM: show empty confidential cards
 */
export default function DualClinicianEmergingNotes({
  showDual = false,
  ssiClinician = null,
  leadClinician = null,
  ssiDisplayName = 'SSI clinician',
  leadDisplayName = 'Lead clinician',
  ssiLastUpdated = null,
  leadLastUpdated = null,
  showConfidential = true,
  singleFormulation = '',
  singleConfidential = '',
  singleLastUpdated = null,
  singleAuthorName = null,
  singleVariant = 'amber',
  alwaysShowConfidential = false,
}) {
  if (showDual) {
    const ssiFormulation = ssiClinician?.formulation || '';
    const leadFormulation = leadClinician?.formulation || '';
    const ssiConfidential = ssiClinician?.confidentialNotes || '';
    const leadConfidential = leadClinician?.confidentialNotes || '';

    return (
      <div className="mb-6 space-y-6">
        {/* Emerging Formulation — two columns for easy comparison / copy */}
        <div>
          <p className="text-sm text-gray-600 mb-3 text-left">
            Working hypotheses from the Semi-Structured Interview clinician and
            the Lead clinician. Not included in the patient report — use these as
            a reference when writing the final formulation.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ReadOnlyNotesCard
              title="Emerging Formulation"
              authorName={ssiDisplayName}
              roleLabel="SSI clinician"
              text={ssiFormulation}
              lastUpdated={ssiLastUpdated}
              emptyMessage="No emerging formulation has been recorded yet."
              ariaLabel={`Emerging formulation from ${ssiDisplayName}`}
            />
            <ReadOnlyNotesCard
              title="Emerging Formulation"
              authorName={leadDisplayName}
              roleLabel="Lead clinician"
              text={leadFormulation}
              lastUpdated={leadLastUpdated}
              emptyMessage="No emerging formulation has been recorded yet."
              ariaLabel={`Emerging formulation from ${leadDisplayName}`}
            />
          </div>
        </div>

        {showConfidential && (
          <div>
            <p className="text-sm text-gray-600 mb-3 text-left">
              Private observations from each clinician. Not included in the
              patient report.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ReadOnlyNotesCard
                title="Confidential Notes"
                authorName={ssiDisplayName}
                roleLabel="SSI clinician"
                text={ssiConfidential}
                lastUpdated={ssiLastUpdated}
                emptyMessage="No confidential notes have been recorded yet."
                ariaLabel={`Confidential notes from ${ssiDisplayName}`}
              />
              <ReadOnlyNotesCard
                title="Confidential Notes"
                authorName={leadDisplayName}
                roleLabel="Lead clinician"
                text={leadConfidential}
                lastUpdated={leadLastUpdated}
                emptyMessage="No confidential notes have been recorded yet."
                ariaLabel={`Confidential notes from ${leadDisplayName}`}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Single-clinician / TOC layout (full width)
  const showConfidentialPanel =
    showConfidential && (alwaysShowConfidential || !!singleConfidential);

  return (
    <div className="mb-6 space-y-6">
      <ReadOnlyNotesCard
        title="Emerging Formulation"
        authorName={singleAuthorName}
        text={singleFormulation}
        lastUpdated={singleLastUpdated}
        emptyMessage="No emerging formulation has been recorded yet."
        ariaLabel="Emerging formulation"
        variant={singleVariant}
        showNonReportFooter={singleVariant === 'amber'}
      />

      {showConfidentialPanel && (
        <ReadOnlyNotesCard
          title="Confidential Notes"
          authorName={singleAuthorName}
          text={singleConfidential}
          lastUpdated={singleLastUpdated}
          emptyMessage="No confidential notes have been recorded yet."
          ariaLabel="Confidential notes"
          variant={singleVariant}
          showNonReportFooter={singleVariant === 'amber'}
        />
      )}
    </div>
  );
}
